import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "src/Store/store";
import { removeItem, updateItemQuantity } from "src/Store/cartSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@mui/material";
import httpRequest from "src/helpers/httpRequest";
import {
  removeItemFromCart,
  setCartItems,
  updateCartQuantity,
} from "src/Store/Cart";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";
import { CartItemToast } from "src/lib/constans";

interface IProps {
  bgColor?: boolean;
  productId: string;
  type: "diagnostic" | "pharmacy";
  updateCount: (val: number) => void;
  buttonDisabled?: boolean;
  handleRemove?: (id: string) => void;
  onlyRemove?: boolean;
  stockQuantity?: number;
  id?: string;
  checkCart?: boolean;
  onCountChange?: (count: number) => void;
}

const CounterComponent: React.FC<IProps> = ({
  bgColor = false,
  productId,
  type,
  updateCount,
  buttonDisabled = false,
  handleRemove,
  onlyRemove = false,
  stockQuantity = 1,
  id = "",
  checkCart = false,
  onCountChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { cartItems } = useSelector((store: RootState) => store.cart);
  const { isLogged } = useSelector((store: RootState) => store.appReducer);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const { pharmacyProducts, diagnosticProducts } = useSelector(
    (store: RootState) => store.cartSlice
  );

  const [count, setCount] = useState<number>(1);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingQuantityRef = useRef<number | null>(null);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);

  const updateProductQuantity = (newQuantity: number) => {
    const isExist = (
      type === "pharmacy"
        ? pharmacyProducts
        : type === "diagnostic"
        ? diagnosticProducts
        : []
    )?.find((v) => v._id === productId);
    if (isExist) {
      const _type =
        type === "pharmacy" ? "pharmacyProducts" : "diagnosticProducts";
      dispatch(updateItemQuantity({ category: _type, productId, newQuantity }));
    }
    updateCount(newQuantity);
  };

  // Debounced API call function
  const debouncedUpdateQuantity = useCallback(
    async (newQuantity: number) => {
      if (isLogged) {
        let item = cartItems.find((v: any) => v.productId === productId);
        if (!item) {
          return;
        }
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Store the latest quantity
        pendingQuantityRef.current = newQuantity;

        // Set a new timeout
        debounceTimeoutRef.current = setTimeout(async () => {
          if (pendingQuantityRef.current !== null) {
            setIsUpdating(true);
            try {
              const { err, res } = await httpRequest({
                path: `/cart/${item?._id}`,
                method: "put",
                params: {
                  quantity: pendingQuantityRef.current,
                  productId: productId,
                  type: item?.type,
                },
              });

              if (err) {
                toast.error(err.message || "Error updating quantity");
                // Revert the count if API fails
                setCount(count);
                return;
              }
              toast.success("Quantity updated successfully");
              updateCount(pendingQuantityRef.current);
              dispatch(updateCartQuantity({ productId, quantity: pendingQuantityRef.current }));
            } catch (error) {
              toast.error("Error updating quantity");
              setCount(count);
            } finally {
              setIsUpdating(false);
              pendingQuantityRef.current = null;
            }
          }
        }, 500); // 500ms delay
      } else {
        updateProductQuantity(newQuantity);
      }
    },
    [productId, type, cartItems, dispatch, updateCount, count, toast]
  );

  const decrement = async () => {
    let newQuantity: number = count > 2 ? count - 1 : 1;
    onCountChange?.(newQuantity);
    setCount(newQuantity);

    if (!isAdded || deleteLoader) {
      return;
    }
    if (isLogged) {
      let item = cartItems.find((v: any) => v.productId === productId);
      if (!item) {
        return;
      }
      if (count === 1 || onlyRemove) {
        setDeleteLoader(true);
        const { err, res } = await httpRequest({
          path: `/cart/${item?._id}`,
          method: "delete",
          params: {},
        });
        if (err) {
          toast.error(CartItemToast["deleteError"][`${type}-product` as keyof typeof CartItemToast["deleteError"]]);
        } else {
          toast.success(CartItemToast["delete"][`${type}-product` as keyof typeof CartItemToast["delete"]]);
          console.log(
            cartItems.filter((v: any) => v.productId !== productId),
            "New Cart Items"
          );
          dispatch(removeItemFromCart([productId]));
          handleRemove?.(productId);
          setDeleteLoader(false);
        }
      } else {
        const newQuantity: number = count - 1;
        setCount(newQuantity);
        debouncedUpdateQuantity(newQuantity);
      }
    } else {
      if (count === 1 || onlyRemove) {
        setDeleteLoader(true);
        dispatch(
          removeItem({
            category:
              type === "pharmacy" ? "pharmacyProducts" : "diagnosticProducts",
            productId,
          })
        );
        handleRemove?.(productId);
      } else {
        const newQuantity: number = count - 1;
        setCount(newQuantity);
        updateProductQuantity(newQuantity);
        setDeleteLoader(false);
      }
    }
  };

  const increment = () => {
    if (stockQuantity === 0) {
      toast.error("This product is out of stock");
      return;
    }
    if (count >= stockQuantity) {
      toast.error("No more stock available");
      return;
    }
    const newQuantity: number = count + 1;
    setCount(newQuantity);

    onCountChange?.(newQuantity);

    if (!isAdded) {
      return;
    }

    if (isLogged) {
      debouncedUpdateQuantity(newQuantity);
    } else {
      updateProductQuantity(newQuantity);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isLogged) {
      const currentProduct = cartItems?.find(
        (v: any) => v.productId === productId
      );
      if (currentProduct) {
        setIsAdded(true);
        setCount(currentProduct?.quantity || 1);
      } else {
        setIsAdded(false);
      }
    } else {
      const currentProduct = (
        type === "pharmacy"
          ? pharmacyProducts
          : type === "diagnostic"
          ? diagnosticProducts
          : []
      )?.find((v) => v._id === productId);
      if (currentProduct) {
        setIsAdded(true);
        setCount(currentProduct?.quantity || 1);
      } else {
        setIsAdded(false);
      }
    }
  }, [
    cartItems,
    productId,
    pharmacyProducts,
    diagnosticProducts,
    type,
    isLogged,
  ]);

  return (
    <>
      {bgColor && !onlyRemove ? (
        <div
          className={`flex justify-center animate-scale-in ${
            buttonDisabled ? "opacity-75" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className={`pharma-checkut-qauntity`}>
            <button
              onClick={decrement}
              disabled={isUpdating}
              className="w-[20px] 2xl:w-[28px] h-[20px] 2xl:h-[28px] flex items-center justify-center group"
              id={id + "-decrement"}
            >
              <FontAwesomeIcon
                className={`${
                  count === 1 ? "rotate-[360deg]" : "rotate-0"
                } group-active:scale-90 text-[clamp(9px,0.8vw,12px)] transition-all text-white ${
                  buttonDisabled ? "cursor-not-allowed" : ""
                }`}
                icon={count === 1 ? faTrash : faMinus}
                size="xs"
              />
            </button>
            <span className="font-size-15px text-[#202020] bg-[#ECECEC] font-Poppins-Medium w-[24px] 2xl:w-[32px] h-[20px] 2xl:h-[28px] flex items-center justify-center">
              {count}
            </span>
            <button
              disabled={buttonDisabled || isUpdating}
              onClick={increment}
              className="w-[20px] 2xl:w-[28px] h-[20px] 2xl:h-[28px] flex items-center justify-center group"
              id={id + "-increment"}
            >
              <FontAwesomeIcon
                className={`text-[clamp(9px,0.8vw,12px)] text-white group-active:scale-90 transition-all ${
                  buttonDisabled ? "cursor-not-allowed " : ""
                }`}
                icon={faPlus}
                size="xs"
              />
            </button>
          </div>
        </div>
      ) : onlyRemove ? (
        <IconButton
          onClick={() => decrement()}
          disabled={isUpdating || deleteLoader}
          id={id + "-remove"}
        >
          {deleteLoader ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <FontAwesomeIcon
              icon={faTrash}
              className="text-[13px] 2xl:text-[15px]"
            />
          )}
        </IconButton>
      ) : (
        <div className="counter-container">
          <button
            className="counter-button text-[#707070] font-Poppins-Medium font-size-16px"
            disabled={count === 1 || buttonDisabled || isUpdating}
            onClick={decrement}
            id={id + "-decrement"}
          >
            -
          </button>
          <div className="counter-display text-[#707070] font-Poppins-Medium font-size-16px">
            {count}
          </div>
          <button
            disabled={buttonDisabled || isUpdating}
            className="counter-button text-[#707070] font-Poppins-Medium font-size-16px"
            onClick={increment}
            id={id + "-increment"}
          >
            +
          </button>
        </div>
      )}
    </>
  );
};

export default CounterComponent;
